import React, { Suspense, lazy } from 'react';
import { Hero } from './components/Hero';

/**
 * WELDING PROJECT PLANS™ sales page.
 * HERO → SNAPSHOT → P26 PREVIEW → PLAN → TECH PROOF → 6 VOLUMES → BONUSES →
 * OFFER → FAQ → FINAL CTA → FOOTER (+ mobile sticky bar after #offer).
 *
 * Everything below the hero loads in separate chunks so the hero (mobile LCP)
 * ships with minimal JS. Each block reserves height while loading.
 */
const named = <K extends string,>(loader: () => Promise<Record<K, React.ComponentType>>, key: K) => lazy(() => loader().then(m => ({
  default: m[key]
})));
const ValueStrip = named(() => import('./components/sections/ValueStrip'), 'ValueStrip');
const ProjectPreviewSection = named(() => import('./components/sections/ProjectPreviewSection'), 'ProjectPreviewSection');
const PlanSection = named(() => import('./components/sections/PlanSection'), 'PlanSection');
const TechProofSection = named(() => import('./components/sections/TechProofSection'), 'TechProofSection');
const CollectionSection = named(() => import('./components/sections/CollectionSection'), 'CollectionSection');
const BonusSection = named(() => import('./components/sections/BonusSection'), 'BonusSection');
const OfferSection = named(() => import('./components/sections/OfferSection'), 'OfferSection');
const FaqSection = named(() => import('./components/sections/FaqSection'), 'FaqSection');
const FinalCta = named(() => import('./components/sections/FinalCta'), 'FinalCta');
const Footer = named(() => import('./components/Footer'), 'Footer');
const StickyBuyBar = named(() => import('./components/StickyBuyBar'), 'StickyBuyBar');
interface DeferredProps {
  children: React.ReactNode;
  minHeight: string;
  background?: string;
  id?: string;
}

/** The fallback carries the section id so anchors resolve even before the chunk loads. */
function Deferred({
  children,
  minHeight,
  background = 'bg-charcoal',
  id
}: DeferredProps) {
  return <Suspense fallback={<div id={id} className={`w-full ${background}`} style={{
    minHeight
  }} aria-hidden data-id="element-1" data-mp="st2i6gybyti7" />} data-id="element-0" data-mp="stkoy7imcq4d">
      {children}
    </Suspense>;
}
export function App() {
  return <main className="w-full overflow-x-clip bg-charcoal text-white" data-id="element-2" data-mp="3udkos1m9b4d1">
      <Hero data-id="element-3" data-mp="d4nwdiwxho3g" />

      <Deferred minHeight="64px" background="bg-surface" data-id="element-4" data-mp="d4e9i476jx1z">
        <ValueStrip data-id="element-5" data-mp="d3waizt4v4qd" />
      </Deferred>
      <Deferred minHeight="900px" id="project-preview" data-id="element-6" data-mp="85ully910b6z">
        <ProjectPreviewSection data-id="element-7" data-mp="86cld4uwd7jn" />
      </Deferred>
      <Deferred minHeight="420px" background="bg-surface" data-id="element-8" data-mp="3gkv1zuywlvev">
        <PlanSection data-id="element-9" data-mp="3gja887keygi2" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-mist" id="technical-proof" data-id="element-10" data-mp="3bmtgz4hx3m0r">
        <TechProofSection data-id="element-11" data-mp="3qb7jtffc3ahb" />
      </Deferred>
      <Deferred minHeight="1000px" id="volumes" data-id="element-12" data-mp="3qbzg6sgr9c8n">
        <CollectionSection data-id="element-13" data-mp="3lkhdnzz333lk" />
      </Deferred>
      <Deferred minHeight="620px" background="bg-surface" id="bonuses" data-id="element-14" data-mp="3ljfzhmmru0zm">
        <BonusSection data-id="element-15" data-mp="2xudaftpt55v3" />
      </Deferred>
      <Deferred minHeight="900px" id="offer" data-id="element-16" data-mp="2xtby7lx22dbs">
        <OfferSection data-id="element-17" data-mp="2sw20zk3fcxde" />
      </Deferred>
      <Deferred minHeight="620px" background="bg-mist" id="faq" data-id="element-18" data-mp="2sv0orcaoa4u3">
        <FaqSection data-id="element-19" data-mp="18e171cv5hagc" />
      </Deferred>
      <Deferred minHeight="480px" background="bg-surface" data-id="element-20" data-mp="18czpcpjmpppn">
        <FinalCta data-id="element-21" data-mp="1d47zjnss9f3v" />
      </Deferred>
      <Deferred minHeight="220px" data-id="element-22" data-mp="1d59jkhrs1bxq">
        <Footer data-id="element-23" data-mp="1d6axcu06gcj5" />
      </Deferred>

      <Suspense fallback={null} data-id="element-24" data-mp="yg3kquwupnqd">
        <StickyBuyBar data-id="element-25" data-mp="yh6erbph06ai" />
      </Suspense>
    </main>;
}